import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, MapPin, Phone, Share2, Twitter, Facebook, Link2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { FadeUp } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { usePageTitle } from "../hooks/usePageTitle";

const DOW = ["S", "M", "T", "W", "T", "F", "S"];

const fmtDate = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }).toUpperCase();

const shareUrl = () => `${window.location.origin}/events`;

const EventCard = ({ e, index, going, onGoing, testId }) => {
  const text = encodeURIComponent(`${e.name} — ${e.date ? fmtDate(e.date) : "DATE TBA"}${e.time ? ` ${e.time}` : ""} · ${e.location} · ARTILLERY KDP`);
  const url = encodeURIComponent(shareUrl());

  const share = async () => {
    const data = { title: `ARTILLERY KDP — ${e.name}`, text: decodeURIComponent(text), url: shareUrl() };
    if (navigator.share) {
      try { await navigator.share(data); return; } catch {}
    }
    await navigator.clipboard.writeText(`${data.text} ${data.url}`);
    toast.success("Event link copied");
  };

  return (
    <FadeUp delay={index * 0.05} className={`border bg-charcoal/40 p-5 ${e.past ? "border-white/5 opacity-55" : "border-white/10"}`} data-testid={testId}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-display text-2xl sm:text-3xl tracking-wide leading-none" data-testid={`${testId}-name`}>{e.name}</p>
          <p className="mt-2 text-xs font-bold tracking-[0.15em] text-pink" data-testid={`${testId}-date`}>
            {e.date ? fmtDate(e.date) : "DATE TO BE CONFIRMED"}{e.time ? ` — ${e.time}` : ""}
          </p>
          {e.location && (
            <p className="mt-1.5 text-xs text-smoke flex items-center gap-1.5" data-testid={`${testId}-location`}>
              <MapPin size={12} className="shrink-0" /> {e.location}
            </p>
          )}
          {e.phone && (
            <a href={`tel:${e.phone}`} className="mt-1.5 text-xs text-smoke flex items-center gap-1.5 hover:text-pink transition-colors duration-200 min-h-[32px]" data-testid={`${testId}-phone`}>
              <Phone size={12} className="shrink-0" /> {e.phone}
            </a>
          )}
          {e.description && <p className="mt-3 text-sm text-bone/75 leading-relaxed" data-testid={`${testId}-description`}>{e.description}</p>}
        </div>
        <span className="font-display text-pink text-xl shrink-0">{String(index + 1).padStart(2, "0")}</span>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {!e.past && (
          <button
            onClick={() => onGoing(e)}
            disabled={going}
            data-testid={`${testId}-going`}
            className={`h-11 px-5 text-[11px] font-extrabold tracking-[0.2em] flex items-center gap-2 transition-colors duration-200 ${
              going ? "bg-acid text-ink" : "bg-bone text-ink hover:bg-pink hover:text-white"
            }`}
          >
            {going && <Check size={14} />} {going ? `GOING · ${e.going}` : `I'M GOING${e.going ? ` · ${e.going}` : ""}`}
          </button>
        )}
        {e.link && (
          <a href={e.link} target="_blank" rel="noopener noreferrer" data-testid={`${testId}-link`}
            className="h-11 px-5 inline-flex items-center border border-white/25 text-[11px] font-extrabold tracking-[0.18em] hover:border-pink hover:text-pink transition-colors duration-200">
            EVENT INFO / DIRECTIONS
          </a>
        )}
        <div className="flex items-center gap-1 ml-auto">
          <button onClick={share} aria-label="Share event" data-testid={`${testId}-share`}
            className="w-11 h-11 border border-white/15 flex items-center justify-center text-smoke hover:border-pink hover:text-pink transition-colors duration-200">
            <Share2 size={15} />
          </button>
          <a href={`https://twitter.com/intent/tweet?text=${text}&url=${url}`} target="_blank" rel="noopener noreferrer" aria-label="Share on X" data-testid={`${testId}-share-x`}
            className="w-11 h-11 border border-white/15 flex items-center justify-center text-smoke hover:border-pink hover:text-pink transition-colors duration-200">
            <Twitter size={15} />
          </a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" data-testid={`${testId}-share-fb`}
            className="w-11 h-11 border border-white/15 flex items-center justify-center text-smoke hover:border-pink hover:text-pink transition-colors duration-200">
            <Facebook size={15} />
          </a>
          <button onClick={async () => { await navigator.clipboard.writeText(`${decodeURIComponent(text)} ${shareUrl()}`); toast.success("Event link copied"); }}
            aria-label="Copy event link" data-testid={`${testId}-share-copy`}
            className="w-11 h-11 border border-white/15 flex items-center justify-center text-smoke hover:border-pink hover:text-pink transition-colors duration-200">
            <Link2 size={15} />
          </button>
        </div>
      </div>
    </FadeUp>
  );
};

export default function Events() {
  usePageTitle("Events — Where to Find ARTILLERY KDP");
  const [events, setEvents] = useState([]);
  const [offset, setOffset] = useState(0);
  const [selectedDay, setSelectedDay] = useState(null);
  const [goingSet, setGoingSet] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("akdp-going") || "[]")); } catch { return new Set(); }
  });

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const now = new Date();
  const view = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const y = view.getFullYear();
  const m = view.getMonth();
  const monthKey = `${y}-${String(m + 1).padStart(2, "0")}`;
  const monthLabel = view.toLocaleDateString("en-US", { month: "long", year: "numeric" }).toUpperCase();

  const firstWeekday = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const todayIso = now.toISOString().slice(0, 10);

  const byDay = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      if (e.date?.startsWith(monthKey)) {
        const d = Number(e.date.slice(8, 10));
        (map[d] = map[d] || []).push(e);
      }
    });
    return map;
  }, [events, monthKey]);

  const monthEvents = events.filter((e) => e.date?.startsWith(monthKey));
  const shown = selectedDay && byDay[selectedDay] ? byDay[selectedDay] : monthEvents;
  const undated = events.filter((e) => !e.date);

  const rsvp = async (e) => {
    if (goingSet.has(e.id)) return;
    try {
      const { data } = await api.post("/events/rsvp", { occurrence_id: e.id });
      setEvents((prev) => prev.map((x) => (x.id === e.id ? { ...x, going: data.count } : x)));
      const next = new Set(goingSet).add(e.id);
      setGoingSet(next);
      localStorage.setItem("akdp-going", JSON.stringify([...next]));
      toast.success("Marked as going");
    } catch {
      toast.error("Something went wrong");
    }
  };

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="pb-20" data-testid="events-page">
      <PageHero slug="piece-22" testId="events-hero">
        <FadeUp>
          <p className="text-[10px] font-bold tracking-[0.3em] text-smoke mb-3">EXHIBITIONS · MARKETS · POP-UPS · APPEARANCES</p>
          <h1 className="font-display text-5xl sm:text-7xl tracking-wide leading-none" data-testid="events-page-title">
            WHERE TO FIND<br />ARTILLERY KDP
          </h1>
        </FadeUp>
      </PageHero>

      <div className="px-5 sm:px-8">
      {undated.length > 0 && (
        <div className="mt-10 space-y-4" data-testid="undated-events">
          {undated.map((e, i) => (
            <EventCard key={e.id} e={e} index={i} going={goingSet.has(e.id)} onGoing={rsvp} testId={`event-tba-${i}`} />
          ))}
        </div>
      )}

      {/* CALENDAR */}
      <FadeUp className="mt-12 border border-white/10" data-testid="events-calendar">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-2">
          <button onClick={() => { setOffset((o) => Math.max(-2, o - 1)); setSelectedDay(null); }} disabled={offset <= -2}
            data-testid="calendar-prev" aria-label="Previous month"
            className="w-11 h-11 flex items-center justify-center disabled:opacity-20 hover:text-pink transition-colors duration-200">
            <ChevronLeft size={20} />
          </button>
          <h2 className="font-display text-2xl sm:text-3xl tracking-wide" data-testid="calendar-month">{monthLabel}</h2>
          <button onClick={() => { setOffset((o) => Math.min(3, o + 1)); setSelectedDay(null); }} disabled={offset >= 3}
            data-testid="calendar-next" aria-label="Next month"
            className="w-11 h-11 flex items-center justify-center disabled:opacity-20 hover:text-pink transition-colors duration-200">
            <ChevronRight size={20} />
          </button>
        </div>
        <div className="grid grid-cols-7 text-center text-[10px] font-bold tracking-widest text-smoke border-b border-white/10">
          {DOW.map((d, i) => <span key={i} className="py-2">{d}</span>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            if (d === null) return <span key={`x${i}`} className="border-b border-r border-white/5 min-h-[52px]" />;
            const iso = `${monthKey}-${String(d).padStart(2, "0")}`;
            const has = !!byDay[d];
            const isToday = iso === todayIso;
            const isSel = selectedDay === d;
            return (
              <button
                key={d}
                onClick={() => setSelectedDay(isSel ? null : d)}
                data-testid={`calendar-day-${d}`}
                aria-label={`${monthLabel} ${d}${has ? ", has events" : ""}`}
                className={`relative min-h-[52px] border-b border-r border-white/5 text-sm font-bold transition-colors duration-150 ${
                  isSel ? "bg-bone text-ink" : has ? "text-pink hover:bg-white/5" : "text-bone/70 hover:bg-white/5"
                }`}
              >
                {d}
                {isToday && <span className="absolute inset-1 border border-pink pointer-events-none" />}
                {has && <span className={`absolute bottom-1.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 ${isSel ? "bg-pink" : "bg-pink"}`} data-testid={`calendar-dot-${d}`} />}
              </button>
            );
          })}
        </div>
      </FadeUp>
      <FadeUp className="mt-3 flex justify-between text-[10px] tracking-[0.25em] text-smoke">
        <span data-testid="calendar-range-note">BROWSE 2 MONTHS BACK — 3 AHEAD</span>
        {selectedDay && <button onClick={() => setSelectedDay(null)} className="text-pink min-h-[32px]" data-testid="calendar-clear-day">SHOW FULL MONTH</button>}
      </FadeUp>

      {/* EVENT LIST */}
      <div className="mt-8 space-y-4" data-testid="month-events">
        {shown.length === 0 && <p className="text-sm text-smoke py-4" data-testid="month-events-empty">No events this month yet.</p>}
        {shown.map((e, i) => (
          <EventCard key={e.id} e={e} index={i} going={goingSet.has(e.id)} onGoing={rsvp} testId={`event-${i}`} />
        ))}
      </div>
      </div>
    </div>
  );
}
